import React, { useState, useEffect, useContext, useRef } from "react";
import Select from "react-select";
import "./CommercialOffer.css";
import { selectStyles } from "../../../../utils/selectStyles";
import VersionHistoryDrawer from "../Estimate/VersionHistoryDrawer/VersionHistoryDrawer";
import CommercialOfferApprovalModal from "./CommercialOfferApprovalModal";
import AddCounterpartyModal from "../../CounterpartiesDatabase/AddCounterpartyModal/AddCounterpartyModal";
import {
  getCommercialProposal,
  saveCpTitle,
  saveCpPaymentSchedule,
  saveCpAdditionalItems,
  saveCpInfoSections,
  requestCommercialProposalApproval,
  getCommercialProposalApprovals,
  cancelCommercialProposalApproval,
  getCpTeis,
  restoreVersion,
} from "../../../../api/projectEstimatesApi";
import {
  getAllCounterparties,
  getImageServiceUrl,
  fetchImageServiceBlobUrl,
  getLegalEntityCounterparty,
  getPhysicalEntityCounterparty,
} from "../../../../api/api";
import { getProjectById } from "../../../../api/projectApi";
import { getAPIURL } from "../../../../api/config";
import { UserContext } from "../../../../contexts/CurrentUserContext";

// Подпись для контрагента в селектах
const getCounterpartyLabel = (c) =>
  c?.short_company_name || c?.full_name || c?.full_company_name || "";
const normalizeId = (id) => (id != null ? String(id) : null);
const toCounterpartyArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.counterparties)) return data.counterparties;
  return [];
};
const getDirectorNameFromCounterparty = (counterparty) => {
  if (!counterparty) return "—";

  const managerName =
    counterparty.full_name_first_supervisor || counterparty.director_full_name;
  if (managerName) return managerName;

  // Для ИП в физлицах часто нет отдельного поля руководителя:
  // берем full_name и очищаем префикс "ИП" и кавычки.
  if (counterparty.type === "physical_entity" && counterparty.full_name) {
    return counterparty.full_name
      .replace(/^\s*ИП\s*/i, "")
      .replace(/[«»"]/g, "")
      .trim();
  }

  return "—";
};

const CREATE_DESIGNER_OPTION = {
  value: "__create_new_designer__",
  label: "+ Создать компанию",
};
const CREATE_RECIPIENT_OPTION = {
  value: "__create_new_recipient__",
  label: "+ Создать контрагента",
};

const getDiscountDisplay = (
  data,
  totalAfterDiscount,
  fallbackPercent = 0,
  fallbackAmount = 0,
) => {
  const percent = Number(data?.discount_percent ?? fallbackPercent) || 0;
  const apiAmount = Number(
    data?.discount_amount ?? data?.discount_sum ?? fallbackAmount,
  );
  const computedFromPercent =
    percent > 0 && percent < 100
      ? totalAfterDiscount / (1 - percent / 100) - totalAfterDiscount
      : 0;
  const amount =
    Number.isFinite(apiAmount) && apiAmount > 0
      ? apiAmount
      : computedFromPercent || Number(fallbackAmount) || 0;
  return {
    percent,
    amount,
    before: totalAfterDiscount + amount,
  };
};

const isNdsIncludedInTotal = (data) => Boolean(data?.nds_included_in_total);

const getProposalQuotedTotal = (data) => {
  if (!data) return 0;
  if (isNdsIncludedInTotal(data)) {
    return (
      Number(
        data.total_with_nds ??
          data.total_without_nds ??
          data.total ??
          data.final_offer_price_without_nds,
      ) || 0
    );
  }
  return (
    Number(
      data.total_without_nds ??
        data.total ??
        data.final_offer_price_without_nds,
    ) || 0
  );
};

const getPaymentScheduleTotal = (data) => {
  if (!data) return 0;
  const withNds = Number(data.total_with_nds);
  if (Number.isFinite(withNds) && withNds > 0) return withNds;
  return getProposalQuotedTotal(data);
};

const PAYMENT_PRICE_ROUND_UNIT = 100;

/** Сумма строк графика сходится со стоимостью работ: остаток округления до сотен садится на последнюю выплату. */
const alignPaymentScheduleToTotal = (schedule, totalAmount) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return schedule || [];
  const total = Number(totalAmount);
  if (!Number.isFinite(total) || total <= 0) return schedule;

  const prices = schedule.map((payment) => Number(payment.price) || 0);
  const sum = prices.reduce((acc, price) => acc + price, 0);
  const delta = total - sum;
  if (delta === 0) return schedule;

  const maxRoundingDust = schedule.length * (PAYMENT_PRICE_ROUND_UNIT / 2);
  if (Math.abs(delta) > maxRoundingDust) return schedule;

  let index = -1;
  for (let i = schedule.length - 1; i >= 0; i -= 1) {
    if (prices[i] > 0) {
      index = i;
      break;
    }
  }
  if (index < 0) return schedule;

  return schedule.map((payment, paymentIndex) =>
    paymentIndex === index
      ? { ...payment, price: prices[paymentIndex] + delta }
      : payment,
  );
};

const getOfferDiscountBase = (data) => {
  if (!data) return 0;
  const after = getProposalQuotedTotal(data);
  const savedAmount = Number(data.discount_amount ?? data.discount_sum) || 0;
  if (savedAmount > 0) return after + savedAmount;
  const savedPercent = Number(data.discount_percent) || 0;
  if (savedPercent > 0 && savedPercent < 100) {
    return after / (1 - savedPercent / 100);
  }
  return after;
};

const getAdditionalItems = (data) => {
  const items = Array.isArray(data?.additional_items)
    ? data.additional_items
    : [];
  return [...items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
};

const getAdditionalItemPrice = (item) =>
  Number(item?.price ?? item?.total ?? item?.amount) || 0;

const mapAdditionalItemToEdit = (item) => {
  const percent = Number(item?.percent ?? item?.percentage) || 0;
  const amount = Number(item?.amount) || 0;
  const hasPercent = percent !== 0;
  return {
    name: item?.name || item?.title || "",
    mode: hasPercent ? "percent" : "amount",
    percent: hasPercent ? percent : 0,
    amount: hasPercent ? 0 : amount,
    price: getAdditionalItemPrice(item),
  };
};

const mapAdditionalItemsToPayload = (items) =>
  items
    .filter((item) => item.name?.trim())
    .map((item, index) => {
      const payload = {
        name: item.name.trim(),
        order_index: index,
      };
      if (item.mode === "percent") {
        payload.percent = Number(item.percent) || 0;
      } else {
        payload.amount = Number(item.amount) || 0;
      }
      return payload;
    });

const DEFAULT_INFO_SECTIONS = [
  {
    title: "Разделы проектирования РП:",
    items: [
      {
        name: "Разработка в стандартном варианте (2D) - ПОС, ОВОС, паспорт объекта, энергетический паспорт, ГП, ТХ, АПТ, сметная документация.",
        description: "",
      },
      {
        name: "Разработка с учетом BIM технологии - АС, КЖ, ВК, ОТ, ВиК, ЭМ, СС, ПС.",
        description: "",
      },
    ],
  },
  {
    title: "В настоящем ценовом предложении не включены затраты на:",
    items: [
      {
        name: "Инженерно-геологические и инженерно-геодезические изыскания.",
        description: "",
      },
      {
        name: "Экспертизу рабочего проекта.",
        description: "",
      },
      {
        name: "Разработку дизайна интерьера и ландшафтного дизайна.",
        description: "",
      },
    ],
  },
];

const DEFAULT_DEADLINE_TEXT =
  "Сроки согласно утвержденному графику проектирования.";
const DEFAULT_PERFORMER_NAME = "Казиев Б.М.";
const DEFAULT_SIGNATURE_DATE = "07.04.2025";

const getDefaultInfoSections = () =>
  DEFAULT_INFO_SECTIONS.map((section, sectionIndex) => ({
    title: section.title,
    order_index: sectionIndex,
    items: section.items.map((item, itemIndex) => ({
      name: item.name,
      description: item.description || "",
      order_index: itemIndex,
    })),
  }));

const normalizeInfoItem = (item, index) => {
  const name = String(item?.name ?? "").trim();
  const description = String(item?.description ?? "").trim();
  return {
    name: name || description,
    description: name ? description : "",
    order_index: item?.order_index ?? index,
  };
};

const normalizeInfoSections = (sections) => {
  if (!Array.isArray(sections)) return [];
  return [...sections]
    .sort((a, b) => (a?.order_index ?? 0) - (b?.order_index ?? 0))
    .map((section, sectionIndex) => ({
      title: String(section?.title ?? ""),
      order_index: section?.order_index ?? sectionIndex,
      items: [...(section?.items || [])]
        .sort((a, b) => (a?.order_index ?? 0) - (b?.order_index ?? 0))
        .map((item, itemIndex) => normalizeInfoItem(item, itemIndex)),
    }));
};

const mapInfoSectionsToPayload = (sections) =>
  (sections || [])
    .map((section, sectionIndex) => {
      const items = (section.items || [])
        .map((item, itemIndex) => {
          const name = String(item?.name ?? "").trim();
          const description = String(item?.description ?? "").trim();
          if (!name && !description) return null;
          return {
            name,
            description,
            order_index: itemIndex,
          };
        })
        .filter(Boolean);
      const title = String(section?.title ?? "").trim();
      if (!title && items.length === 0) return null;
      return {
        title,
        items,
        order_index: sectionIndex,
      };
    })
    .filter(Boolean);

const formatDocumentDateForDisplay = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  return trimmed;
};

const formatDocumentDateForApi = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const dmy = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(trimmed);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return null;
};

const resolveAdditionalInfoFromProposal = (cd) => {
  const sectionsFromApi = cd?.info_sections;
  const deadlineFromApi = cd?.deadline ?? cd?.deadlines_text;
  const performerFromApi = cd?.performer_name;
  const dateFromApi = cd?.document_date ?? cd?.signature_date;

  return {
    infoSections: Array.isArray(sectionsFromApi)
      ? normalizeInfoSections(sectionsFromApi)
      : getDefaultInfoSections(),
    deadlinesText:
      deadlineFromApi != null && String(deadlineFromApi).trim() !== ""
        ? String(deadlineFromApi)
        : DEFAULT_DEADLINE_TEXT,
    performerName:
      performerFromApi != null && String(performerFromApi).trim() !== ""
        ? String(performerFromApi)
        : DEFAULT_PERFORMER_NAME,
    signatureDate:
      dateFromApi != null && String(dateFromApi).trim() !== ""
        ? formatDocumentDateForDisplay(dateFromApi)
        : DEFAULT_SIGNATURE_DATE,
  };
};

const getSurchargeSectionNumbers = (data, additionalCount) => {
  let next = 2;
  const bim = Number(data?.bim_surcharge?.percent) > 0 ? next++ : null;
  const acceleration =
    Number(data?.acceleration_surcharge?.percent) > 0 ? next++ : null;
  const smeta = next++;
  const costOfWorks = next++;
  const additionalStart = next;
  next += additionalCount;
  return {
    bim,
    acceleration,
    smeta,
    costOfWorks,
    additionalStart,
    grandTotal: next,
  };
};

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getBlockBindingCoeff = (block) => {
  const value = Number(block?.coefficient);
  return Number.isFinite(value) ? value : 1;
};

const getBlockFloorCoeff = (block) => {
  const value = Number(block?.floor_coeff);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const BUILDING_TYPES_WITHOUT_FLOOR_COEFF = new Set(["ОЗ", "ИЖД"]);

const shouldHideFloorCoefficient = (buildingType) =>
  BUILDING_TYPES_WITHOUT_FLOOR_COEFF.has(String(buildingType || "").trim());

const isParkingBlock = (block) => {
  const blockName = block?.block?.name || block?.name || "";
  return blockName.toLowerCase().includes("паркинг");
};

const roundMoneyToTens = (value) => Math.round(value / 10) * 10;

const getBlockUnitPrice = (block) => {
  const rate = toFiniteNumber(block?.price_per_m2);
  if (isParkingBlock(block)) {
    return roundMoneyToTens(rate);
  }
  const raw = rate * getBlockBindingCoeff(block) * getBlockFloorCoeff(block);
  return roundMoneyToTens(raw);
};

const roundMoneyToHundreds = (value) => Math.round(value / 100) * 100;

const getBlockRowTotal = (block) =>
  roundMoneyToHundreds(
    toFiniteNumber(block?.total_area) * getBlockUnitPrice(block),
  );

const getProjectDocumentationTotal = (blocks) =>
  (blocks || []).reduce((sum, block) => sum + getBlockRowTotal(block), 0);

const SIGNATURE_LAYOUT_STORAGE_KEY = "commercialOfferSignatureLayout";
const defaultSignatureLayout = {
  signature: { x: 6, y: 48, w: 190, h: 62 },
  seal: { x: 116, y: 6, w: 150, h: 150 },
};

const selectPercentInputValue = (event) => {
  const input = event.target;
  requestAnimationFrame(() => input.select());
};

const CommercialOffer = ({ projectId, projectLockedByApproval = false }) => {
  const { userData } = useContext(UserContext);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [commercialData, setCommercialData] = useState(null);
  const [designersCounterparties, setDesignersCounterparties] = useState([]); // our_company — генпроектировщики
  const [recipientsCounterparties, setRecipientsCounterparties] = useState([]); // counterparty — получатели
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [isCancellingApproval, setIsCancellingApproval] = useState(false);

  // Состояния для редактирования полей
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingPaymentSchedule, setIsEditingPaymentSchedule] =
    useState(false);
  const [isEditingAdditionalItems, setIsEditingAdditionalItems] =
    useState(false);
  const [tempProjectName, setTempProjectName] = useState("");
  const [tempTitle, setTempTitle] = useState("");
  const [tempPaymentSchedule, setTempPaymentSchedule] = useState([]);
  const [tempAdditionalItems, setTempAdditionalItems] = useState([]);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingPaymentSchedule, setIsSavingPaymentSchedule] = useState(false);
  const [isSavingAdditionalItems, setIsSavingAdditionalItems] = useState(false);
  const [calculatingFields, setCalculatingFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoErrors, setLogoErrors] = useState(new Set());
  const [loadingLogos, setLoadingLogos] = useState(new Set());
  const [designerLogoSrc, setDesignerLogoSrc] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isEditable, setIsEditable] = useState(true); // Флаг редактируемости коммерческого предложения
  const [cpApprovalStatus, setCpApprovalStatus] = useState(null); // Статус согласования КП
  const [isCreatingCopy, setIsCreatingCopy] = useState(false); // Флаг создания копии
  const [showAddDesignerModal, setShowAddDesignerModal] = useState(false);
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);

  // Редактируемые блоки доп. информации (от «Разделы проектирования РП» до реквизитов) и подписи
  const [infoSections, setInfoSections] = useState(getDefaultInfoSections);
  const [deadlinesText, setDeadlinesText] = useState(DEFAULT_DEADLINE_TEXT);
  const [performerName, setPerformerName] = useState(DEFAULT_PERFORMER_NAME);
  const [signatureDate, setSignatureDate] = useState(DEFAULT_SIGNATURE_DATE);
  const [isSavingAdditionalInfo, setIsSavingAdditionalInfo] = useState(false);
  const [isEditingAdditionalInfo, setIsEditingAdditionalInfo] = useState(false);
  const [signatureLayout, setSignatureLayout] = useState(
    defaultSignatureLayout,
  );
  const signatureDragRef = useRef(null);
  const loadedDesignerDetailsRef = useRef(new Set());
  const logoObjectUrlRef = useRef(null);

  // При активном согласовании сметы или КП блокируем редактирование (флаг с родительской страницы)
  const effectiveIsEditable = isEditable && !projectLockedByApproval;

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SIGNATURE_LAYOUT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.signature && parsed?.seal) {
          setSignatureLayout({
            signature: {
              ...defaultSignatureLayout.signature,
              ...parsed.signature,
            },
            seal: {
              ...defaultSignatureLayout.seal,
              ...parsed.seal,
            },
          });
        }
      }
    } catch (err) {
      console.warn("Не удалось прочитать настройки подписи/печати:", err);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        SIGNATURE_LAYOUT_STORAGE_KEY,
        JSON.stringify(signatureLayout),
      );
    } catch (err) {
      console.warn("Не удалось сохранить настройки подписи/печати:", err);
    }
  }, [signatureLayout]);

  const updateSignatureLayout = (kind, field, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return;
    setSignatureLayout((prev) => ({
      ...prev,
      [kind]: {
        ...prev[kind],
        [field]: num,
      },
    }));
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = signatureDragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      setSignatureLayout((prev) => {
        const current = prev[drag.kind];
        if (!current) return prev;

        if (drag.mode === "move") {
          return {
            ...prev,
            [drag.kind]: {
              ...current,
              x: drag.origin.x + dx,
              y: drag.origin.y + dy,
            },
          };
        }

        return {
          ...prev,
          [drag.kind]: {
            ...current,
            w: Math.max(24, drag.origin.w + dx),
            h: Math.max(24, drag.origin.h + dy),
          },
        };
      });
    };

    const handleMouseUp = () => {
      signatureDragRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startSignatureDrag = (kind, mode) => (e) => {
    if (!effectiveIsEditable) return;
    e.preventDefault();
    e.stopPropagation();
    signatureDragRef.current = {
      kind,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...signatureLayout[kind] },
    };
  };

  // Вспомогательная функция для определения редактируемости на основе версий и статусов
  const determineEditable = (commercialData, approvals, teisData) => {
    // Получаем текущую версию из данных КП
    const currentVersion = commercialData?.version;

    // Получаем максимальную версию из списка согласований
    const maxApprovalVersion =
      approvals && approvals.length > 0
        ? Math.max(
            ...approvals
              .map((a) => a.version)
              .filter((v) => v !== null && v !== undefined),
          )
        : null;

    // Сравниваем версии: если текущая версия больше максимальной из списка согласований,
    // то можно не блокировать редактирование
    const isVersionNewer =
      currentVersion !== null &&
      currentVersion !== undefined &&
      maxApprovalVersion !== null &&
      currentVersion > maxApprovalVersion;

    // Устанавливаем статус согласования КП из ТЭП
    if (teisData) {
      // Блокируем редактирование если статус "согласован", но не блокируем если версия новее
      const isApproved = teisData.cp_approval_status === "approved";
      return (
        commercialData.editable !== false && (!isApproved || isVersionNewer)
      );
    } else {
      // Устанавливаем флаг редактируемости из API
      // Если версия новее максимальной из согласований, разрешаем редактирование
      return commercialData.editable !== false || isVersionNewer;
    }
  };

  const enrichCommercialDataWithTeis = (commercial, teisData) => {
    if (!commercial) return commercial;

    let next = commercial;
    if (Array.isArray(commercial.project_documentation)) {
      const teisBlocks = Array.isArray(teisData?.blocks) ? teisData.blocks : [];
      if (teisBlocks.length > 0) {
        const teisByBlockId = new Map(
          teisBlocks
            .filter((b) => b?.block?.id != null)
            .map((b) => [String(b.block.id), b]),
        );
        next = {
          ...commercial,
          project_documentation: commercial.project_documentation.map(
            (block) => {
              const blockId = block?.block?.id;
              const teisBlock =
                blockId != null ? teisByBlockId.get(String(blockId)) : null;
              return {
                ...block,
                floor_coeff: teisBlock?.floor_coeff ?? block?.floor_coeff ?? 0,
              };
            },
          ),
        };
      }
    }

    return next;
  };

  const applyAdditionalInfoFromProposal = (cd) => {
    const next = resolveAdditionalInfoFromProposal(cd);
    setInfoSections(next.infoSections);
    setDeadlinesText(next.deadlinesText);
    setPerformerName(next.performerName);
    setSignatureDate(next.signatureDate);
  };

  // Загрузка данных коммерческого предложения и компаний
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        console.log("projectId не передан:", projectId);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log("Загружаем данные для проекта:", projectId);

        const [
          commercialData,
          ourCompanies,
          counterpartiesList,
          projectDataResponse,
          approvals,
          teisData,
        ] = await Promise.all([
          getCommercialProposal(projectId),
          getAllCounterparties({ counterparty_side: "our_company" }),
          getAllCounterparties({ counterparty_side: "counterparty" }),
          getProjectById(projectId),
          getCommercialProposalApprovals(projectId).catch(() => []),
          getCpTeis(projectId).catch(() => null),
        ]);

        console.log("Полученные данные:", commercialData);
        console.log("Контрагенты (наши компании):", ourCompanies);
        console.log("Контрагенты (получатели):", counterpartiesList);
        console.log("Данные проекта:", projectDataResponse);
        console.log("Согласования:", approvals);
        console.log("Данные ТЭП:", teisData);
        console.log(
          "smeta_documentation_percent:",
          commercialData?.smeta_documentation_percent,
        );
        console.log(
          "project_documentation блоки:",
          commercialData?.project_documentation,
        );
        // Логируем данные паркинга для отладки
        if (commercialData?.project_documentation) {
          const parkingBlock = commercialData.project_documentation.find(
            (block) => block.block?.name?.toLowerCase().includes("паркинг"),
          );
          if (parkingBlock) {
            console.log("Данные паркинга из API:", {
              name: parkingBlock.block?.name,
              floor_count: parkingBlock.floor_count,
              total_area: parkingBlock.total_area,
              coefficient: parkingBlock.coefficient,
            });
          }
        }

        const enriched = enrichCommercialDataWithTeis(commercialData, teisData);
        setCommercialData(enriched);
        const ourList = toCounterpartyArray(ourCompanies);
        const recipientsList = toCounterpartyArray(counterpartiesList);
        setDesignersCounterparties(ourList);
        setRecipientsCounterparties(recipientsList);
        setProjectData(projectDataResponse);

        // Генпроектировщик: из КП (counterparty_id / counterparty) или первый из наших компаний
        const counterpartyFromOffer = commercialData?.counterparty;
        const counterpartyIdFromOffer = commercialData?.counterparty_id;
        if (ourList.length > 0) {
          let designer = null;
          if (counterpartyIdFromOffer != null) {
            designer = ourList.find(
              (c) => normalizeId(c.id) === normalizeId(counterpartyIdFromOffer),
            );
          } else if (counterpartyFromOffer) {
            // Ищем по полям из нового формата API
            designer = ourList.find(
              (c) =>
                (counterpartyFromOffer.short_company_name &&
                  c.short_company_name ===
                    counterpartyFromOffer.short_company_name) ||
                (counterpartyFromOffer.full_company_name &&
                  c.full_company_name ===
                    counterpartyFromOffer.full_company_name) ||
                (counterpartyFromOffer.full_name &&
                  c.full_name === counterpartyFromOffer.full_name),
            );
          }
          const designerToSelect = designer || ourList[0];
          setSelectedDesigner({
            value: normalizeId(designerToSelect.id),
            label: getCounterpartyLabel(designerToSelect),
          });
        }

        // Получатель: из КП (receiver_id / receiver) или первый из контрагентов
        const receiverFromOffer = commercialData?.receiver;
        const receiverIdFromOffer = commercialData?.receiver_id;
        if (recipientsList.length > 0) {
          let recipient = null;
          if (receiverIdFromOffer != null) {
            recipient = recipientsList.find(
              (c) => normalizeId(c.id) === normalizeId(receiverIdFromOffer),
            );
          } else if (receiverFromOffer) {
            // Ищем по полям из нового формата API (full_name, full_company_name, short_company_name)
            const receiverName =
              receiverFromOffer.short_company_name ||
              receiverFromOffer.full_company_name ||
              receiverFromOffer.full_name ||
              receiverFromOffer.name; // fallback для старого формата
            if (receiverName) {
              recipient = recipientsList.find(
                (c) =>
                  c.short_company_name === receiverName ||
                  c.full_company_name === receiverName ||
                  c.full_name === receiverName,
              );
            }
          }
          const recipientToSelect = recipient || recipientsList[0];
          setSelectedRecipient({
            value: normalizeId(recipientToSelect.id),
            label: getCounterpartyLabel(recipientToSelect),
          });
        }

        // Редактируемые блоки доп. информации и подписи
        applyAdditionalInfoFromProposal(commercialData);
        // Ставка НДС и способ расчёта из данных КП
        const cd = commercialData;
        if (cd?.nds_percent != null) setNdsRate(cd.nds_percent);
        if (cd?.nds_included_in_total != null) {
          setNdsIncludedInTotal(Boolean(cd.nds_included_in_total));
        }
        const discountSource =
          cd?.discount_percent != null ||
          cd?.discount_amount != null ||
          cd?.discount_sum != null
            ? cd
            : teisData;
        if (discountSource) {
          if (discountSource.discount_percent != null) {
            setDiscountPercent(Number(discountSource.discount_percent) || 0);
          }
          if (
            discountSource.discount_amount != null ||
            discountSource.discount_sum != null
          ) {
            setDiscountAmount(
              Number(
                discountSource.discount_amount ?? discountSource.discount_sum,
              ) || 0,
            );
          }
          const percent = Number(discountSource.discount_percent) || 0;
          const amount =
            Number(
              discountSource.discount_amount ?? discountSource.discount_sum,
            ) || 0;
          if (percent > 0) setDiscountMode("percent");
          else if (amount > 0) setDiscountMode("amount");
        }

        setApprovalStatuses(approvals || []);

        // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
        if (teisData && teisData.cp_approval_status) {
          setCpApprovalStatus(teisData.cp_approval_status);
        } else if (commercialData && commercialData.cp_approval_status) {
          setCpApprovalStatus(commercialData.cp_approval_status);
        } else {
          setCpApprovalStatus(null);
        }

        // Определяем редактируемость на основе версий и статусов
        setIsEditable(
          determineEditable(commercialData, approvals || [], teisData),
        );
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  useEffect(() => {
    loadedDesignerDetailsRef.current.clear();
  }, [projectId]);

  useEffect(() => {
    const id = selectedDesigner?.value;
    if (!id) return undefined;

    const existing = designersCounterparties.find(
      (c) => normalizeId(c.id) === id,
    );
    if (!existing) return undefined;
    if (existing.company_logo_image_name) return undefined;
    if (loadedDesignerDetailsRef.current.has(id)) return undefined;

    loadedDesignerDetailsRef.current.add(id);
    let cancelled = false;

    const loadCompanyDetails = async () => {
      try {
        const details =
          existing.type === "physical_entity"
            ? await getPhysicalEntityCounterparty(id)
            : await getLegalEntityCounterparty(id);
        if (cancelled || !details) return;
        setDesignersCounterparties((prev) =>
          prev.map((c) =>
            normalizeId(c.id) === id ? { ...c, ...details } : c,
          ),
        );
      } catch (err) {
        loadedDesignerDetailsRef.current.delete(id);
        console.error("Не удалось загрузить данные компании для логотипа:", err);
      }
    };
    loadCompanyDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedDesigner?.value, designersCounterparties]);

  // Выбранный контрагент — генпроектировщик (наша компания)
  const selectedDesignerCounterparty = selectedDesigner
    ? designersCounterparties.find(
        (c) => normalizeId(c.id) === selectedDesigner.value,
      )
    : designersCounterparties[0];
  const selectedDesignerDirectorName = getDirectorNameFromCounterparty(
    selectedDesignerCounterparty,
  );
  const showDirectorTitle =
    selectedDesignerCounterparty?.type !== "physical_entity";

  // Выбранный контрагент — получатель
  const selectedRecipientCounterparty = selectedRecipient
    ? recipientsCounterparties.find(
        (c) => normalizeId(c.id) === selectedRecipient.value,
      )
    : recipientsCounterparties[0];

  const formatCounterpartyAddress = (c) => {
    if (!c) return "";
    const parts = [
      c.postal_code,
      c.country,
      c.city,
      c.street,
      c.building,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Функция для формирования URL логотипа компании
  const getCompanyLogoUrl = (counterparty) => {
    if (!counterparty) return null;

    if (counterparty.company_logo_image_name) {
      return getImageServiceUrl(counterparty.company_logo_image_name);
    }

    const logoUrl = counterparty.logo_url;
    if (!logoUrl) return null;

    let imageUrl = logoUrl;

    if (!imageUrl.startsWith("http")) {
      if (imageUrl.startsWith("/")) {
        imageUrl = `${getAPIURL().replace(/\/$/, "")}${imageUrl}`;
      } else {
        imageUrl = `${getAPIURL()}images/company_logos/${imageUrl}`;
      }
    }

    return decodeURIComponent(imageUrl);
  };

  const designerLogoUrl = getCompanyLogoUrl(selectedDesignerCounterparty);

  useEffect(() => {
    const fileName = selectedDesignerCounterparty?.company_logo_image_name;
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
    setDesignerLogoSrc(null);

    if (!fileName) {
      const legacyUrl = getCompanyLogoUrl(selectedDesignerCounterparty);
      if (legacyUrl) setDesignerLogoSrc(legacyUrl);
      return undefined;
    }

    let cancelled = false;
    const loadLogo = async () => {
      try {
        const blobUrl = await fetchImageServiceBlobUrl(fileName);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        logoObjectUrlRef.current = blobUrl;
        setDesignerLogoSrc(blobUrl);
      } catch (err) {
        console.error("Не удалось загрузить логотип компании:", err);
        if (!cancelled) {
          setDesignerLogoSrc(null);
        }
      }
    };
    loadLogo();

    return () => {
      cancelled = true;
    };
  }, [
    selectedDesignerCounterparty?.id,
    selectedDesignerCounterparty?.company_logo_image_name,
    selectedDesignerCounterparty?.logo_url,
  ]);

  // Обработчик ошибки загрузки логотипа
  const handleLogoError = (companyId) => {
    setLogoErrors((prev) => new Set([...prev, companyId]));
  };

  // Функции для редактирования полей
  const handleStartEdit = () => {
    if (!effectiveIsEditable) return; // Не редактируем если редактирование заблокировано
    setTempProjectName(commercialData?.project_name || "");
    setTempTitle(commercialData?.title || "");
    setIsEditingProjectName(true);
    setIsEditingTitle(true);
  };

  const handleStartEditPaymentSchedule = () => {
    if (!effectiveIsEditable) return; // Не редактируем если редактирование заблокировано
    const existingSchedule = alignPaymentScheduleToTotal(
      commercialData?.cp_payment_schedule || [],
      getPaymentScheduleTotal(commercialData),
    );
    setTempPaymentSchedule(existingSchedule.map((payment) => ({ ...payment })));
    setIsEditingPaymentSchedule(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProjectName(false);
    setIsEditingTitle(false);
    setTempProjectName("");
    setTempTitle("");
  };

  const handleCancelEditPaymentSchedule = () => {
    setIsEditingPaymentSchedule(false);
    setTempPaymentSchedule([]);
  };

  const handleSaveTitle = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    if (!tempProjectName.trim() || !tempTitle.trim()) {
      alert("Пожалуйста, заполните все поля");
      return;
    }

    setIsSavingTitle(true);
    try {
      const dataToSave = {
        project_name: tempProjectName.trim(),
        title: tempTitle.trim(),
        performer_name: performerName.trim(),
        ...getNdsSaveFields(),
        ...getDiscountSaveFields(),
      };
      if (selectedDesigner?.value)
        dataToSave.counterparty_id = Number(selectedDesigner.value);
      if (selectedRecipient?.value)
        dataToSave.receiver_id = Number(selectedRecipient.value);
      console.log("Отправка данных для сохранения CP title:", {
        projectId,
        data: dataToSave,
      });

      const result = await saveCpTitle(projectId, dataToSave);

      // Обновляем локальные данные, включая версию из ответа
      setCommercialData((prev) => ({
        ...prev,
        project_name: tempProjectName.trim(),
        title: tempTitle.trim(),
        performer_name: performerName.trim(),
        ...(selectedDesigner?.value && {
          counterparty_id: Number(selectedDesigner.value),
        }),
        ...(selectedRecipient?.value && {
          receiver_id: Number(selectedRecipient.value),
        }),
        ...(result?.version !== undefined && { version: result.version }),
      }));

      setIsEditingProjectName(false);
      setIsEditingTitle(false);
      setTempProjectName("");
      setTempTitle("");
    } catch (error) {
      console.error("Ошибка при сохранении:", error);
      const errorMessage =
        error.message || "Неизвестная ошибка при сохранении данных";
      alert(`Ошибка при сохранении данных: ${errorMessage}`);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleSavePaymentSchedule = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    if (tempPaymentSchedule.length === 0) {
      alert("Пожалуйста, добавьте хотя бы один элемент в график платежей");
      return;
    }

    // Проверяем, что все поля заполнены
    const hasEmptyFields = tempPaymentSchedule.some(
      (payment) =>
        !payment.work_name?.trim() ||
        payment.percentage === undefined ||
        payment.percentage === null ||
        payment.price === undefined ||
        payment.price === null ||
        !payment.deadline_description?.trim(),
    );

    if (hasEmptyFields) {
      alert("Пожалуйста, заполните все поля в графике платежей");
      return;
    }

    setIsSavingPaymentSchedule(true);
    try {
      await saveCpPaymentSchedule(projectId, tempPaymentSchedule);
      await reloadCommercialProposal();

      setIsEditingPaymentSchedule(false);
      setTempPaymentSchedule([]);
    } catch (error) {
      console.error("Ошибка при сохранении графика платежей:", error);
      alert("Ошибка при сохранении графика платежей");
    } finally {
      setIsSavingPaymentSchedule(false);
    }
  };

  const handleStartEditAdditionalItems = () => {
    if (!effectiveIsEditable) return;
    const existing = getAdditionalItems(commercialData);
    setTempAdditionalItems(
      existing.length > 0
        ? existing.map(mapAdditionalItemToEdit)
        : [mapAdditionalItemToEdit({ name: "", amount: 0 })],
    );
    setIsEditingAdditionalItems(true);
  };

  const handleCancelEditAdditionalItems = () => {
    setIsEditingAdditionalItems(false);
    setTempAdditionalItems([]);
  };

  const addAdditionalItem = () => {
    if (!effectiveIsEditable) return;
    setTempAdditionalItems((prev) => [
      ...prev,
      mapAdditionalItemToEdit({ name: "", amount: 0 }),
    ]);
  };

  const removeAdditionalItem = (index) => {
    if (!effectiveIsEditable) return;
    setTempAdditionalItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAdditionalItem = (index, field, value) => {
    if (!effectiveIsEditable) return;
    setTempAdditionalItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleAdditionalItemModeChange = (index, nextMode) => {
    if (!effectiveIsEditable) return;
    setTempAdditionalItems((prev) =>
      prev.map((item, i) => {
        if (i !== index || item.mode === nextMode) return item;
        return {
          ...item,
          mode: nextMode,
          percent: nextMode === "percent" ? item.percent || 0 : 0,
          amount: nextMode === "amount" ? item.amount || 0 : 0,
        };
      }),
    );
  };

  const handleSaveAdditionalItems = async () => {
    if (!effectiveIsEditable || !projectId) return;
    const incomplete = tempAdditionalItems.some(
      (item) =>
        !item.name?.trim() &&
        (Number(item.amount) > 0 || Number(item.percent) > 0),
    );
    if (incomplete) {
      alert("Укажите название для каждой надбавки");
      return;
    }

    setIsSavingAdditionalItems(true);
    try {
      const payload = mapAdditionalItemsToPayload(tempAdditionalItems);
      await saveCpAdditionalItems(projectId, payload);
      await reloadCommercialProposal();
      setIsEditingAdditionalItems(false);
      setTempAdditionalItems([]);
    } catch (error) {
      console.error("Ошибка при сохранении надбавок:", error);
      alert(error.message || "Ошибка при сохранении надбавок");
    } finally {
      setIsSavingAdditionalItems(false);
    }
  };

  // Функции для управления элементами графика платежей
  const addPaymentItem = () => {
    if (!effectiveIsEditable) return; // Не добавляем если редактирование заблокировано
    setTempPaymentSchedule([
      ...tempPaymentSchedule,
      {
        work_name: "",
        percentage: 0,
        price: 0,
        deadline_description: "",
      },
    ]);
  };

  const removePaymentItem = (index) => {
    if (!effectiveIsEditable) return; // Не удаляем если редактирование заблокировано
    setTempPaymentSchedule(tempPaymentSchedule.filter((_, i) => i !== index));
  };

  // Получаем общую сумму для расчета процентов
  const getTotalAmount = () => getPaymentScheduleTotal(commercialData);

  // Вычисляем площади для сметной документации с округлением
  const calculateSmetaAreas = () => {
    if (!commercialData) return { residentialArea: 0, parkingArea: 0 };

    // Получаем суммы сметной документации
    const residentialSum = commercialData.smeta_documentation?.residential || 0;
    const parkingSum = commercialData.smeta_documentation?.parking || 0;
    const smetaPercent = commercialData.smeta_documentation_percent || 0;

    // Используем процент с бэка как цену за единицу для расчета площадей
    // Суммы приходят округленные с бэка, вычисляем площади обратно
    let residentialArea = 0;
    let parkingArea = 0;

    if (smetaPercent > 0) {
      if (residentialSum > 0) {
        residentialArea = Math.round(residentialSum / smetaPercent);
      }

      if (parkingSum > 0) {
        parkingArea = Math.round(parkingSum / smetaPercent);
      }
    }

    return { residentialArea, parkingArea };
  };

  // Обработчик фокуса для числовых полей
  const handleNumberFieldFocus = (e) => {
    if (e.target.value === "0") {
      const input = e.target;
      requestAnimationFrame(() => input.select());
    }
  };

  // Обработчик ввода для числовых полей
  const handleNumberFieldInput = (index, field, e) => {
    let value = e.target.value;

    // Убираем ведущие нули
    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      value = value.replace(/^0+/, "");
      if (value === "") value = "0";
    }

    updatePaymentItem(index, field, parseFloat(value) || 0);
  };

  // Обновляем элемент с автоматическим пересчетом
  const updatePaymentItem = (index, field, value) => {
    if (!effectiveIsEditable) return; // Не обновляем если редактирование заблокировано
    const totalAmount = getTotalAmount();

    // Добавляем анимацию для пересчитываемого поля
    const calculatedField = field === "percentage" ? "price" : "percentage";
    const fieldKey = `${index}-${calculatedField}`;

    setCalculatingFields((prev) => new Set([...prev, fieldKey]));

    // Убираем анимацию через 300ms
    setTimeout(() => {
      setCalculatingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }, 300);

    setTempPaymentSchedule(
      tempPaymentSchedule.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };

          // Если изменился процент - пересчитываем сумму
          if (field === "percentage") {
            const percentage = Math.round(parseFloat(value) || 0);
            updatedItem.percentage = percentage;
            updatedItem.price = roundMoneyToHundreds(
              (totalAmount * percentage) / 100,
            );
          }

          // Если изменилась сумма - пересчитываем процент
          if (field === "price") {
            const price = parseFloat(value) || 0;
            updatedItem.price = price;
            updatedItem.percentage =
              totalAmount > 0 ? Math.round((price / totalAmount) * 100) : 0;
          }

          return updatedItem;
        }
        return item;
      }),
    );
  };

  // Данные для селектов: генпроектировщики и получатели из контрагентов
  const designerOptions = [
    CREATE_DESIGNER_OPTION,
    ...designersCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getCounterpartyLabel(c),
    })),
  ];

  const recipientOptions = [
    CREATE_RECIPIENT_OPTION,
    ...recipientsCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getCounterpartyLabel(c),
    })),
  ];

  const [ndsRate, setNdsRate] = useState(12);
  const [ndsIncludedInTotal, setNdsIncludedInTotal] = useState(true);
  const [isSavingNdsRate, setIsSavingNdsRate] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountMode, setDiscountMode] = useState("percent");
  const [isSavingDiscountPercent, setIsSavingDiscountPercent] = useState(false);

  const getDiscountSaveFields = () =>
    discountMode === "amount"
      ? { discount_amount: Number(discountAmount) || 0, discount_percent: 0 }
      : { discount_percent: Number(discountPercent) || 0, discount_amount: 0 };

  const getNdsSaveFields = (overrides = {}) => ({
    nds_percent: ndsRate,
    nds_included_in_total: ndsIncludedInTotal,
    ...overrides,
  });

  const handleDiscountModeChange = (nextMode) => {
    if (nextMode === discountMode) return;
    const base = getOfferDiscountBase(commercialData);
    if (nextMode === "amount" && base > 0) {
      setDiscountAmount(
        Math.round((base * (Number(discountPercent) || 0)) / 100),
      );
    }
    if (nextMode === "percent" && base > 0) {
      setDiscountPercent(
        Math.round(((Number(discountAmount) || 0) / base) * 10000) / 100,
      );
    }
    setDiscountMode(nextMode);
  };

  const syncDiscountFromData = (data, extraData) => {
    const source =
      data?.discount_percent != null ||
      data?.discount_amount != null ||
      data?.discount_sum != null
        ? data
        : extraData;
    if (!source) return;
    if (source.discount_percent != null) {
      setDiscountPercent(Number(source.discount_percent) || 0);
    }
    if (source.discount_amount != null || source.discount_sum != null) {
      setDiscountAmount(
        Number(source.discount_amount ?? source.discount_sum) || 0,
      );
    }
    const percent = Number(source.discount_percent) || 0;
    const amount = Number(source.discount_amount ?? source.discount_sum) || 0;
    if (percent > 0) setDiscountMode("percent");
    else if (amount > 0) setDiscountMode("amount");
  };

  const reloadCommercialProposal = async () => {
    const updatedData = await getCommercialProposal(projectId);
    const updatedTeisData = await getCpTeis(projectId).catch(() => null);
    const enriched = enrichCommercialDataWithTeis(updatedData, updatedTeisData);
    setCommercialData(enriched);
    syncDiscountFromData(updatedData, updatedTeisData);
    if (updatedData?.nds_percent != null) setNdsRate(updatedData.nds_percent);
    if (updatedData?.nds_included_in_total != null) {
      setNdsIncludedInTotal(Boolean(updatedData.nds_included_in_total));
    }
    applyAdditionalInfoFromProposal(updatedData);
    return enriched;
  };

  const saveCpTitleAndReload = async (ndsOverrides = {}) => {
    const dataToSave = {
      project_name: commercialData?.project_name ?? "",
      title: commercialData?.title ?? "",
      performer_name: performerName.trim(),
      ...getNdsSaveFields(ndsOverrides),
      ...getDiscountSaveFields(),
    };
    if (selectedDesigner?.value)
      dataToSave.counterparty_id = Number(selectedDesigner.value);
    if (selectedRecipient?.value)
      dataToSave.receiver_id = Number(selectedRecipient.value);
    await saveCpTitle(projectId, dataToSave);
    return reloadCommercialProposal();
  };

  const handleNdsIncludedChange = async (checked) => {
    if (!projectId || !effectiveIsEditable) return;
    const previous = ndsIncludedInTotal;
    setNdsIncludedInTotal(checked);
    setIsSavingNdsRate(true);
    try {
      await saveCpTitleAndReload({ nds_included_in_total: checked });
    } catch (error) {
      console.error("Ошибка при сохранении способа расчёта НДС:", error);
      setNdsIncludedInTotal(previous);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setIsSavingNdsRate(false);
    }
  };

  // Сохранение выбора генпроектировщика и получателя на бэкенд
  const saveDesignerAndRecipient = async () => {
    if (
      !projectId ||
      !selectedDesigner?.value ||
      !selectedRecipient?.value ||
      !effectiveIsEditable
    )
      return;
    try {
      const dataToSave = {
        project_name: commercialData?.project_name ?? "",
        title: commercialData?.title ?? "",
        counterparty_id: Number(selectedDesigner.value),
        receiver_id: Number(selectedRecipient.value),
        performer_name: performerName.trim(),
        ...getNdsSaveFields(),
        ...getDiscountSaveFields(),
      };
      const result = await saveCpTitle(projectId, dataToSave);
      setCommercialData((prev) => ({
        ...prev,
        counterparty_id: Number(selectedDesigner.value),
        receiver_id: Number(selectedRecipient.value),
        ...(result?.version !== undefined && { version: result.version }),
      }));
    } catch (err) {
      console.error("Ошибка сохранения генпроектировщика/получателя:", err);
      alert(err?.message || "Ошибка сохранения");
    }
  };

  const handleSaveAdditionalInfo = async () => {
    if (!projectId || !effectiveIsEditable) return;
    setIsSavingAdditionalInfo(true);
    try {
      const sectionsPayload = mapInfoSectionsToPayload(infoSections);
      await saveCpInfoSections(projectId, sectionsPayload);
      setInfoSections(normalizeInfoSections(sectionsPayload));
      setCommercialData((prev) =>
        prev ? { ...prev, info_sections: sectionsPayload } : prev,
      );

      const dataToSave = {
        project_name: commercialData?.project_name ?? "",
        title: commercialData?.title ?? "",
        performer_name: performerName.trim(),
        deadline: deadlinesText.trim(),
        ...getNdsSaveFields(),
        ...getDiscountSaveFields(),
      };
      const documentDate = formatDocumentDateForApi(signatureDate);
      if (documentDate) dataToSave.document_date = documentDate;
      if (selectedDesigner?.value)
        dataToSave.counterparty_id = Number(selectedDesigner.value);
      if (selectedRecipient?.value)
        dataToSave.receiver_id = Number(selectedRecipient.value);

      try {
        await saveCpTitle(projectId, dataToSave);
      } catch (titleError) {
        if (!dataToSave.deadline && !dataToSave.document_date) throw titleError;
        const titleWithoutDates = { ...dataToSave };
        delete titleWithoutDates.deadline;
        delete titleWithoutDates.document_date;
        await saveCpTitle(projectId, titleWithoutDates);
      }

      await reloadCommercialProposal();
      setIsEditingAdditionalInfo(false);
    } catch (err) {
      console.error("Ошибка сохранения доп. информации:", err);
      alert(err?.message || "Ошибка сохранения");
    } finally {
      setIsSavingAdditionalInfo(false);
    }
  };

  const handleCancelEditAdditionalInfo = () => {
    applyAdditionalInfoFromProposal(commercialData);
    setIsEditingAdditionalInfo(false);
  };

  const updateInfoSection = (sectionIndex, updater) => {
    setInfoSections((prev) =>
      prev.map((section, index) =>
        index === sectionIndex ? updater(section) : section,
      ),
    );
  };

  const addInfoSection = () => {
    setInfoSections((prev) => [
      ...prev,
      {
        title: "",
        order_index: prev.length,
        items: [{ name: "", description: "", order_index: 0 }],
      },
    ]);
  };

  const removeInfoSection = (sectionIndex) => {
    setInfoSections((prev) => prev.filter((_, index) => index !== sectionIndex));
  };

  const addInfoSectionItem = (sectionIndex) => {
    updateInfoSection(sectionIndex, (section) => ({
      ...section,
      items: [
        ...(section.items || []),
        {
          name: "",
          description: "",
          order_index: (section.items || []).length,
        },
      ],
    }));
  };

  const updateInfoSectionItem = (sectionIndex, itemIndex, field, value) => {
    updateInfoSection(sectionIndex, (section) => ({
      ...section,
      items: (section.items || []).map((item, index) =>
        index === itemIndex ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const removeInfoSectionItem = (sectionIndex, itemIndex) => {
    updateInfoSection(sectionIndex, (section) => ({
      ...section,
      items: (section.items || []).filter((_, index) => index !== itemIndex),
    }));
  };

  const handleApprovalSubmit = async (selectedApprovers, comment) => {
    if (!projectId || !commercialData) {
      alert("Ошибка: отсутствуют данные проекта или коммерческого предложения");
      return;
    }

    setIsSubmittingApproval(true);
    try {
      const totalPrice = getProposalQuotedTotal(commercialData);

      console.log("Отправка на согласование:", {
        projectId,
        approverIds: selectedApprovers,
        totalPrice,
        comment,
      });

      const result = await requestCommercialProposalApproval(
        projectId,
        selectedApprovers,
        totalPrice,
        comment,
      );

      console.log("Результат отправки на согласование:", result);
      console.log("Версия из ответа API:", result?.version);

      // Обновляем статусы согласования
      const statuses = await getCommercialProposalApprovals(projectId);
      console.log("Полученные статусы согласований:", statuses);
      console.log(
        "Версии в статусах:",
        statuses?.map((s) => ({ id: s.id, version: s.version })),
      );
      setApprovalStatuses(statuses || []);

      // Перезагружаем данные коммерческого предложения, чтобы обновился флаг editable
      const updatedData = await getCommercialProposal(projectId);
      const updatedTeisData = await getCpTeis(projectId).catch(() => null);

      setCommercialData(
        enrichCommercialDataWithTeis(updatedData, updatedTeisData),
      );
      // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
      if (updatedTeisData && updatedTeisData.cp_approval_status) {
        setCpApprovalStatus(updatedTeisData.cp_approval_status);
      } else if (updatedData && updatedData.cp_approval_status) {
        setCpApprovalStatus(updatedData.cp_approval_status);
      } else {
        setCpApprovalStatus(null);
      }
      setIsEditable(
        determineEditable(updatedData, statuses || [], updatedTeisData),
      );

      // Показываем успешное сообщение
      alert("Коммерческое предложение успешно отправлено на согласование!");

      // Закрываем модальное окно
      setShowApprovalModal(false);
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
      alert(`Ошибка при отправке на согласование: ${error.message}`);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return (
        <span className="commercial-offer-approval-status-icon approved">
          ✓
        </span>
      );
    }
    if (statusLower === "на рассмотрении" || statusLower === "pending") {
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return (
        <span className="commercial-offer-approval-status-icon rejected">
          ✕
        </span>
      );
    }
    return (
      <span className="commercial-offer-approval-status-icon pending">⏳</span>
    );
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return "Согласован";
    }
    if (
      statusLower === "на рассмотрении" ||
      statusLower === "pending" ||
      statusLower === "на рассмотрении"
    ) {
      return "На рассмотрении";
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return "Отклонен";
    }
    return "На рассмотрении";
  };

  // Обработчик отмены согласования
  const handleCancelApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку на согласование?",
      )
    ) {
      return;
    }

    try {
      setIsCancellingApproval(true);
      await cancelCommercialProposalApproval(parseInt(projectId));

      // Обновляем статусы согласования с кэш-бастингом
      const statuses = await getCommercialProposalApprovals(projectId);
      setApprovalStatuses(statuses || []);

      // Перезагружаем данные коммерческого предложения, чтобы обновился флаг editable
      const updatedData = await getCommercialProposal(projectId);
      const updatedTeisData = await getCpTeis(projectId).catch(() => null);

      setCommercialData(
        enrichCommercialDataWithTeis(updatedData, updatedTeisData),
      );
      // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
      if (updatedTeisData && updatedTeisData.cp_approval_status) {
        setCpApprovalStatus(updatedTeisData.cp_approval_status);
      } else if (updatedData && updatedData.cp_approval_status) {
        setCpApprovalStatus(updatedData.cp_approval_status);
      } else {
        setCpApprovalStatus(null);
      }
      setIsEditable(
        determineEditable(updatedData, statuses || [], updatedTeisData),
      );

      // Показываем успешное сообщение
      alert("Отправка на согласование успешно отозвана!");

      // Перезагружаем страницу через небольшую задержку, чтобы пользователь увидел сообщение
      // Используем жесткую перезагрузку с кэш-бастингом для гарантии свежих данных
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    } catch (error) {
      console.error("Ошибка при отмене согласования:", error);
      alert(`Ошибка при отмене согласования: ${error.message}`);
    } finally {
      setIsCancellingApproval(false);
    }
  };

  // Проверяем, отменено ли согласование инициатором по статусу из данных КП
  const isCancelledByInitiator =
    cpApprovalStatus &&
    (cpApprovalStatus.toLowerCase() === "отменен инициатором" ||
      cpApprovalStatus.toLowerCase() === "cancelled_by_initiator");

  // Находим активное согласование (только то, которое еще не завершено)
  // Если согласование отменено инициатором по статусу из КП, не ищем активное согласование
  const activeApproval = isCancelledByInitiator
    ? null
    : approvalStatuses.find((approval) => {
        const status = approval.status?.toLowerCase() || "";
        if (
          status === "согласован" ||
          status === "отклонен" ||
          status === "отменен инициатором" ||
          status === "не согласован"
        ) {
          return false; // Согласование уже завершено или отменено
        }

        // Если статус "на рассмотрении" - это активное согласование
        if (status === "на рассмотрении") {
          return true;
        }

        // Проверяем шаги согласования
        if (approval.steps && approval.steps.length > 0) {
          return approval.steps.some((step) => {
            const stepStatus = step.status?.toLowerCase() || "";
            return (
              stepStatus === "на рассмотрении" ||
              stepStatus === "" ||
              step.status === null ||
              step.status === undefined
            );
          });
        }

        // Если шагов нет, но статус не завершен, считаем активным
        return !approval.status || status === "";
      });

  // Проверяем, является ли текущий пользователь инициатором согласования
  const isInitiator =
    activeApproval && userData && activeApproval.initiator_id === userData.id;

  // Показываем кнопку отмены только если:
  // 1. Есть активное согласование
  // 2. Пользователь является инициатором
  // 3. Статус согласования не "отменен инициатором"
  const showCancelButton =
    activeApproval !== undefined &&
    activeApproval !== null &&
    isInitiator &&
    !isCancelledByInitiator;

  // Проверяем, передан ли projectId
  if (!projectId) {
    return (
      <div className="commercial-offer-container">
        <div className="error">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  const additionalItemsForDisplay = isEditingAdditionalItems
    ? tempAdditionalItems
    : getAdditionalItems(commercialData);
  const surchargeNums = getSurchargeSectionNumbers(
    commercialData,
    additionalItemsForDisplay.length,
  );
  const additionalItemsTotal = additionalItemsForDisplay.reduce(
    (sum, item) => sum + getAdditionalItemPrice(item),
    0,
  );
  const hideFloorCoefficient = shouldHideFloorCoefficient(
    projectData?.building_type,
  );
  const tableColCount = hideFloorCoefficient ? 8 : 9;

  return (
    <div className="commercial-offer-container">
      {/* Параметры коммерческого предложения */}
      <div className="commercial-offer-params">
        <div className="commercial-offer-top-bar">
          <h2 className="commercial-offer-title">
            Параметры коммерческого предложения
          </h2>
          <div className="commercial-offer-top-bar-right">
            {isCancelledByInitiator && (
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginRight: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "20px" }}>ℹ️</span>
                <span style={{ color: "#856404", fontSize: "14px" }}>
                  Согласование коммерческого предложения отменено инициатором
                </span>
              </div>
            )}
            {showCancelButton && (
              <>
                {activeApproval.steps && activeApproval.steps.length > 0 && (
                  <div className="commercial-offer-approval-status-block">
                    {activeApproval.comment?.trim() && (
                      <div className="commercial-offer-approval-comment">
                        <span className="commercial-offer-approval-comment-label">
                          Описание:
                        </span>
                        <span>{activeApproval.comment}</span>
                      </div>
                    )}
                    {activeApproval.steps.map((step, index) => (
                      <div
                        key={step.id || index}
                        className="commercial-offer-approval-step"
                      >
                        <span className="commercial-offer-approver-name">
                          {step.approver_name || "Не указан"}:
                        </span>
                        {getStatusIcon(step.status)}
                        <span className="commercial-offer-step-status-text">
                          {getStatusText(step.status)}
                        </span>
                        {step.comment && (
                          <span className="commercial-offer-step-comment">
                            ({step.comment})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="commercial-offer-cancel-approval-btn"
                  onClick={handleCancelApproval}
                  disabled={isCancellingApproval}
                >
                  {isCancellingApproval ? "Отзыв..." : "Отозвать отправку"}
                </button>
              </>
            )}
            <div className="commercial-offer-version-badge-container">
              {commercialData?.version !== undefined &&
                commercialData?.version !== null && (
                  <span className="commercial-offer-version-badge">
                    Версия {commercialData.version}
                  </span>
                )}
              <button
                className="commercial-offer-history-btn"
                onClick={() => setIsVersionHistoryOpen(true)}
              >
                <span className="commercial-offer-history-icon">⟳</span> История
                версий
              </button>
            </div>
          </div>
        </div>
        {/* Предупреждение о блокировке редактирования */}
        {!effectiveIsEditable && (
          <div
            style={{
              background: "#fff8f1",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "24px" }}>⚠️</span>
              <span style={{ color: "#b26a00", fontSize: "14px" }}>
                Коммерческое предложение находится на этапе согласования,
                поэтому редактирование заблокировано
              </span>
            </div>
            {cpApprovalStatus === "approved" && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Вы уверены, что хотите создать копию? Будет создана новая версия ТЭП, Сметы и КП без согласований.",
                    )
                  ) {
                    return;
                  }

                  try {
                    setIsCreatingCopy(true);
                    await restoreVersion(projectId, null);
                    // Перезагружаем страницу после создания копии
                    setTimeout(() => {
                      window.location.reload();
                    }, 500);
                  } catch (error) {
                    console.error("Ошибка при создании копии:", error);
                    alert(`Ошибка при создании копии: ${error.message}`);
                  } finally {
                    setIsCreatingCopy(false);
                  }
                }}
                disabled={isCreatingCopy}
                style={{
                  padding: "8px 16px",
                  background: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: isCreatingCopy ? "not-allowed" : "pointer",
                  opacity: isCreatingCopy ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {isCreatingCopy ? "Создание копии..." : "Сделать копию"}
              </button>
            )}
          </div>
        )}

        <div className="params-fields">
          <div className="param-field">
            <label>Выберите генпроектировщика*</label>
            <Select
              styles={selectStyles}
              options={designerOptions}
              value={selectedDesigner}
              onChange={(val) => {
                if (val?.value === CREATE_DESIGNER_OPTION.value) {
                  setShowAddDesignerModal(true);
                  return;
                }
                setSelectedDesigner(val);
                if (val?.value && selectedRecipient?.value) {
                  saveDesignerAndRecipient();
                }
              }}
              isDisabled={!effectiveIsEditable}
              placeholder="Выберите проектировщика..."
              formatOptionLabel={(option) =>
                option.value === CREATE_DESIGNER_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>

          <div className="param-field">
            <label>Выберите компанию-получателя*</label>
            <Select
              styles={selectStyles}
              options={recipientOptions}
              value={selectedRecipient}
              onChange={(val) => {
                if (val?.value === CREATE_RECIPIENT_OPTION.value) {
                  setShowAddRecipientModal(true);
                  return;
                }
                setSelectedRecipient(val);
                if (val?.value && selectedDesigner?.value) {
                  saveDesignerAndRecipient();
                }
              }}
              isDisabled={!effectiveIsEditable}
              placeholder="Выберите получателя..."
              formatOptionLabel={(option) =>
                option.value === CREATE_RECIPIENT_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>
        </div>

        <div className="params-fields-second-row">
          <div className="param-field">
            <label>Ставка НДС (%)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="number"
                value={ndsRate}
                onChange={(e) => setNdsRate(parseFloat(e.target.value) || 0)}
                onFocus={selectPercentInputValue}
                onClick={selectPercentInputValue}
                min="0"
                max="100"
                step="1"
                disabled={!effectiveIsEditable || isSavingNdsRate}
                style={{
                  width: "100px",
                  padding: "8px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={async () => {
                  if (!projectId || !effectiveIsEditable) return;
                  setIsSavingNdsRate(true);
                  try {
                    await saveCpTitleAndReload();
                    alert("Ставка НДС сохранена");
                  } catch (error) {
                    console.error("Ошибка при сохранении ставки НДС:", error);
                    alert(`Ошибка: ${error.message}`);
                  } finally {
                    setIsSavingNdsRate(false);
                  }
                }}
                disabled={!effectiveIsEditable || isSavingNdsRate}
                style={{
                  padding: "8px 16px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor:
                    !effectiveIsEditable || isSavingNdsRate
                      ? "not-allowed"
                      : "pointer",
                  opacity: !effectiveIsEditable || isSavingNdsRate ? 0.6 : 1,
                }}
              >
                {isSavingNdsRate ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
            <label
              className={`nds-included-toggle${
                !effectiveIsEditable || isSavingNdsRate ? " disabled" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={ndsIncludedInTotal}
                onChange={(e) => handleNdsIncludedChange(e.target.checked)}
                disabled={!effectiveIsEditable || isSavingNdsRate}
              />
              НДС включен в общую сумму
            </label>
            <div className="nds-included-hint">
              {ndsIncludedInTotal
                ? "НДС выделяется из итоговой суммы проектирования"
                : "НДС добавляется сверху к итоговой сумме проектирования"}
            </div>
          </div>
          <div className="param-field">
            <label>Скидка</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div className="discount-mode-toggle">
                <button
                  type="button"
                  className={discountMode === "percent" ? "active" : ""}
                  onClick={() => handleDiscountModeChange("percent")}
                  disabled={!effectiveIsEditable || isSavingDiscountPercent}
                >
                  %
                </button>
                <button
                  type="button"
                  className={discountMode === "amount" ? "active" : ""}
                  onClick={() => handleDiscountModeChange("amount")}
                  disabled={!effectiveIsEditable || isSavingDiscountPercent}
                >
                  тг
                </button>
              </div>
              <input
                type="number"
                value={
                  discountMode === "amount" ? discountAmount : discountPercent
                }
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  const next = Number.isFinite(val) && val >= 0 ? val : 0;
                  if (discountMode === "amount") {
                    setDiscountAmount(next);
                  } else {
                    setDiscountPercent(Math.min(next, 100));
                  }
                }}
                onFocus={selectPercentInputValue}
                onClick={selectPercentInputValue}
                min="0"
                max={discountMode === "percent" ? "100" : undefined}
                step={discountMode === "percent" ? "0.01" : "1"}
                disabled={!effectiveIsEditable || isSavingDiscountPercent}
                style={{
                  width: discountMode === "amount" ? "140px" : "100px",
                  padding: "8px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={async () => {
                  if (!projectId || !effectiveIsEditable) return;
                  const base = getOfferDiscountBase(commercialData);
                  if (
                    discountMode === "amount" &&
                    base > 0 &&
                    Number(discountAmount) > base
                  ) {
                    alert("Сумма скидки не может быть больше итоговой суммы");
                    return;
                  }
                  setIsSavingDiscountPercent(true);
                  try {
                    await saveCpTitleAndReload();
                    alert("Скидка сохранена");
                  } catch (error) {
                    console.error("Ошибка при сохранении скидки:", error);
                    alert(`Ошибка: ${error.message}`);
                  } finally {
                    setIsSavingDiscountPercent(false);
                  }
                }}
                disabled={!effectiveIsEditable || isSavingDiscountPercent}
                style={{
                  padding: "8px 16px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor:
                    !effectiveIsEditable || isSavingDiscountPercent
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    !effectiveIsEditable || isSavingDiscountPercent ? 0.6 : 1,
                }}
              >
                {isSavingDiscountPercent ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
            {discountMode === "percent" &&
            getOfferDiscountBase(commercialData) > 0 ? (
              <div className="discount-hint">
                ≈{" "}
                {Math.round(
                  (getOfferDiscountBase(commercialData) *
                    (Number(discountPercent) || 0)) /
                    100,
                ).toLocaleString("ru-RU")}{" "}
                тг
              </div>
            ) : discountMode === "amount" &&
              getOfferDiscountBase(commercialData) > 0 ? (
              <div className="discount-hint">
                ≈{" "}
                {(
                  ((Number(discountAmount) || 0) /
                    getOfferDiscountBase(commercialData)) *
                  100
                ).toFixed(2)}
                % от суммы
              </div>
            ) : null}
          </div>
        </div>

        <div className="commercial-offer-links">
          <h3 className="offer-title">Коммерческое предложение</h3>
          {/* <div className="mode-toggle">
            <div className="mode-buttons">
              <button
                className={`mode-btn ${!isEditMode ? "active" : ""}`}
                onClick={() => setIsEditMode(false)}
                title="Просмотр"
                disabled={!effectiveIsEditable}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 2C4.5 2 1.5 4.5 1.5 8C1.5 11.5 4.5 14 8 14C11.5 14 14.5 11.5 14.5 8C14.5 4.5 11.5 2 8 2ZM8 12C5.5 12 3.5 10 3.5 8C3.5 6 5.5 4 8 4C10.5 4 12.5 6 12.5 8C12.5 10 10.5 12 8 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 6C7.5 6 7 6.5 7 7C7 7.5 7.5 8 8 8C8.5 8 9 7.5 9 7C9 6.5 8.5 6 8 6Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={`mode-btn ${isEditMode ? "active" : ""}`}
                onClick={() => setIsEditMode(true)}
                title="Редактирование"
                disabled={!effectiveIsEditable}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 2L14 4L5 13H2V10L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div> */}
        </div>
      </div>

      {/* Документ коммерческого предложения */}
      <div className="commercial-offer-document">
        <div className="document-header">
          <div className="sender-info-latin">
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            {selectedDesignerCounterparty?.organization_type?.kz_name && (
              <div className="company-type">
                {selectedDesignerCounterparty.organization_type.kz_name}
              </div>
            )}
            <div className="company-address">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              {selectedDesignerCounterparty?.website && (
                <div>{selectedDesignerCounterparty.website}</div>
              )}
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>

          <div className="document-logo">
            {designerLogoSrc && (
              <div className="logo-container">
                <img
                  src={designerLogoSrc}
                  alt={getCounterpartyLabel(selectedDesignerCounterparty)}
                  onError={() => setDesignerLogoSrc(null)}
                />
              </div>
            )}
          </div>

          <div className="sender-info-cyrillic">
            {selectedDesignerCounterparty?.organization_type?.name && (
              <div className="company-type-ru">
                {selectedDesignerCounterparty.organization_type.name}
              </div>
            )}
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            <div className="company-address-ru">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              {selectedDesignerCounterparty?.website && (
                <div>{selectedDesignerCounterparty.website}</div>
              )}
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>
        </div>

        <div className="document-bottom">
          <div className="outgoing-info">
            <div className="outgoing-number">
              <strong>исх. №</strong> _________________
            </div>
            <div className="outgoing-date">
              <strong>от</strong> "__" ______ 20__ г.
            </div>
          </div>
          <div className="recipient-company">
            <strong>
              {selectedRecipientCounterparty
                ? getCounterpartyLabel(selectedRecipientCounterparty)
                : ""}
            </strong>
          </div>
        </div>

        <div className="document-title">КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ</div>

        <div className="document-content">
          <p className="text-center">
            Настоящим выражаем свое почтение и признательность за возможность
            участвовать в проекте по строительству объекта:
          </p>
          <div className="text-center project-name">
            {isEditingProjectName ? (
              <div className="editable-field">
                <label className="edit-label">Название проекта:</label>
                <input
                  type="text"
                  value={tempProjectName}
                  onChange={(e) => setTempProjectName(e.target.value)}
                  className="edit-input"
                  placeholder="Введите название проекта"
                  disabled={!effectiveIsEditable}
                />
              </div>
            ) : (
              <div className="display-field">
                <span>
                  "
                  {commercialData?.project_name ||
                    "Многоквартирный жилой комплекс со встроенными помещениями и паркингом, расположенный по адресу: г. Астана, Есильский район, улица N"}
                  "
                </span>
                <button
                  className="edit-btn"
                  onClick={handleStartEdit}
                  title="Редактировать проект"
                  disabled={!effectiveIsEditable}
                  style={{
                    cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                    opacity: !effectiveIsEditable ? 0.5 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 2L14 4L5 13H2V10L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="text-center">
            {isEditingTitle ? (
              <div className="editable-field">
                <label className="edit-label">Описание проекта:</label>
                <textarea
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="edit-textarea"
                  placeholder="Введите описание проекта"
                  rows="3"
                  disabled={!effectiveIsEditable}
                />
              </div>
            ) : (
              <div className="display-field">
                <span>
                  {commercialData?.title ||
                    "После мониторинга объекта и общей площади этажа предлагаем разработать рабочий проект в количестве 5 блоков и паркинга"}
                </span>
                <button
                  className="edit-btn"
                  onClick={handleStartEdit}
                  title="Редактировать проект"
                  disabled={!effectiveIsEditable}
                  style={{
                    cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                    opacity: !effectiveIsEditable ? 0.5 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 2L14 4L5 13H2V10L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Кнопки сохранения/отмены */}
          {(isEditingProjectName || isEditingTitle) && (
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={handleSaveTitle}
                disabled={!effectiveIsEditable || isSavingTitle}
              >
                {isSavingTitle ? (
                  "Сохранение..."
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 2L6 9L3 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Сохранить
                  </>
                )}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEdit}
                disabled={!effectiveIsEditable || isSavingTitle}
              >
                Отмена
              </button>
            </div>
          )}
          <div className="document-cost">
            <strong>
              за стоимость{" "}
              {(commercialData?.nds_percent > 0
                ? commercialData?.total_with_nds ||
                  commercialData?.total_without_nds ||
                  commercialData?.total
                : commercialData?.total_without_nds || commercialData?.total
              )?.toLocaleString("ru-RU") || "0"}{" "}
              тенге
              {commercialData?.nds_percent > 0
                ? isNdsIncludedInTotal(commercialData)
                  ? ` с учетом НДС (${commercialData.nds_percent}%), НДС включен в сумму`
                  : ` с учетом НДС (${commercialData.nds_percent}%)`
                : " без НДС"}
            </strong>
          </div>
        </div>

        {/* Таблица работ */}
        <div
          className={`work-table ${isEditingAdditionalItems ? "editing" : ""}${
            hideFloorCoefficient ? " no-floor-coeff" : ""
          }`}
        >
          <div className="payment-table-header">
            <div>
              <h3>Наименование работ</h3>
              {isEditingAdditionalItems && (
                <div className="total-amount-info">
                  <span className="total-amount-label">
                    Процент считается от стоимости проектных работ с учётом
                    сметной документации. После сохранения суммы пересчитаются.
                  </span>
                </div>
              )}
            </div>
            {!isEditingAdditionalItems && (
              <button
                className="edit-payment-schedule-btn"
                onClick={handleStartEditAdditionalItems}
                title="Редактировать надбавки"
                disabled={!effectiveIsEditable}
                style={{
                  cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                  opacity: !effectiveIsEditable ? 0.6 : 1,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 2L14 4L5 13H2V10L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Редактировать надбавки
              </button>
            )}
          </div>
          {loading ? (
            <div className="loading">Загрузка данных...</div>
          ) : error ? (
            <div className="error">Ошибка загрузки: {error}</div>
          ) : commercialData ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Наименование работ</th>
                    <th>Часть здания</th>
                    <th>Площадь, м2</th>
                    <th>Ставка за м2</th>
                    <th>Коэффициент привязки</th>
                    {!hideFloorCoefficient && (
                      <th>Коэффициент этажности</th>
                    )}
                    <th>Цена за ед.</th>
                    <th>ИТОГО</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Проектная документация */}
                  <tr className="commercial-section-header">
                    <td>1</td>
                    <td>Разработка проектной документации</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {!hideFloorCoefficient && <td></td>}
                    <td></td>
                    <td></td>
                  </tr>
                  {commercialData.project_documentation?.map((block, index) => (
                    <tr
                      key={block.block.id}
                      className={
                        index % 2 === 0 ? "table-row" : "table-row-alt"
                      }
                    >
                      <td></td>
                      <td></td>
                      <td>
                        {block.block.name} - {block.floor_count}эт
                      </td>
                      <td>
                        {block.total_area?.toLocaleString("ru-RU") || "0"}
                      </td>
                      <td>
                        {block.price_per_m2?.toLocaleString("ru-RU") || ""}
                      </td>
                      <td>
                        {isParkingBlock(block)
                          ? ""
                          : block.coefficient?.toFixed(2) || "1,00"}
                      </td>
                      {!hideFloorCoefficient && (
                        <td>
                          {isParkingBlock(block)
                            ? ""
                            : (block.floor_coeff ?? 0).toFixed(2)}
                        </td>
                      )}
                      <td>
                        {getBlockUnitPrice(block).toLocaleString("ru-RU", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        {getBlockRowTotal(block).toLocaleString("ru-RU") || "0"}
                      </td>
                    </tr>
                  ))}
                  <tr className="section-total">
                    <td colSpan="3">Итого:</td>
                    <td>
                      {commercialData.project_documentation
                        ?.reduce(
                          (sum, block) => sum + (block.total_area || 0),
                          0,
                        )
                        .toLocaleString("ru-RU")}
                    </td>
                    <td></td>
                    <td></td>
                    {!hideFloorCoefficient && <td></td>}
                    <td></td>
                    <td>
                      {getProjectDocumentationTotal(
                        commercialData.project_documentation,
                      ).toLocaleString("ru-RU")}
                    </td>
                  </tr>
                  {/* Системные надбавки: от проектирования, без сметной документации */}
                  {Number(commercialData.bim_surcharge?.percent) > 0 ? (
                    <tr className="commercial-section-header">
                      <td>{surchargeNums.bim}</td>
                      <td>
                        Надбавка за проектирование рабочего проекта по BIM
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {!hideFloorCoefficient && <td></td>}
                      <td>+{commercialData.bim_surcharge.percent}%</td>
                      <td>
                        {commercialData.bim_surcharge.price?.toLocaleString(
                          "ru-RU",
                        ) || "0"}
                      </td>
                    </tr>
                  ) : null}
                  {Number(commercialData.acceleration_surcharge?.percent) >
                  0 ? (
                    <tr className="commercial-section-header">
                      <td>{surchargeNums.acceleration}</td>
                      <td>Надбавка за ускорение</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {!hideFloorCoefficient && <td></td>}
                      <td>+{commercialData.acceleration_surcharge.percent}%</td>
                      <td>
                        {commercialData.acceleration_surcharge.price?.toLocaleString(
                          "ru-RU",
                        ) || "0"}
                      </td>
                    </tr>
                  ) : null}
                  {/* Сметная документация */}
                  <tr className="commercial-section-header">
                    <td>{surchargeNums.smeta}</td>
                    <td>Разработка сметной документации</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {!hideFloorCoefficient && <td></td>}
                    <td></td>
                    <td></td>
                  </tr>
                  {(() => {
                    const { residentialArea, parkingArea } =
                      calculateSmetaAreas();
                    return (
                      <>
                        {commercialData.smeta_documentation?.residential &&
                        commercialData.smeta_documentation.residential > 0 ? (
                          <tr className="table-row">
                            <td></td>
                            <td></td>
                            <td>Жилая часть</td>
                            <td>
                              {residentialArea > 0
                                ? residentialArea.toLocaleString("ru-RU")
                                : ""}
                            </td>
                            <td>
                              {commercialData.smeta_documentation_percent
                                ? `${commercialData.smeta_documentation_percent}`
                                : ""}
                            </td>
                            <td></td>
                            {!hideFloorCoefficient && <td></td>}
                            <td></td>
                            <td>
                              {commercialData.smeta_documentation.residential?.toLocaleString(
                                "ru-RU",
                              ) || "0"}
                            </td>
                          </tr>
                        ) : null}
                        {commercialData.smeta_documentation?.parking &&
                        commercialData.smeta_documentation.parking > 0 ? (
                          <tr className="table-row-alt">
                            <td></td>
                            <td></td>
                            <td>Паркинг</td>
                            <td>
                              {parkingArea > 0
                                ? parkingArea.toLocaleString("ru-RU")
                                : ""}
                            </td>
                            <td>
                              {commercialData.smeta_documentation_percent
                                ? `${commercialData.smeta_documentation_percent}`
                                : ""}
                            </td>
                            <td></td>
                            {!hideFloorCoefficient && <td></td>}
                            <td></td>
                            <td>
                              {commercialData.smeta_documentation.parking?.toLocaleString(
                                "ru-RU",
                              ) || "0"}
                            </td>
                          </tr>
                        ) : null}
                      </>
                    );
                  })()}
                  <tr className="section-total">
                    <td colSpan="3">Итого:</td>
                    <td>
                      {(() => {
                        const { residentialArea, parkingArea } =
                          calculateSmetaAreas();
                        const totalArea = residentialArea + parkingArea;
                        return totalArea > 0
                          ? totalArea.toLocaleString("ru-RU")
                          : "";
                      })()}
                    </td>
                    <td></td>
                    <td></td>
                    {!hideFloorCoefficient && <td></td>}
                    <td></td>
                    <td>
                      {(
                        (commercialData.smeta_documentation?.residential || 0) +
                        (commercialData.smeta_documentation?.parking || 0)
                      ).toLocaleString("ru-RU")}
                    </td>
                  </tr>
                  {/* Итоговая сумма и произвольные надбавки */}
                  {(() => {
                    const ndsIncluded = isNdsIncludedInTotal(commercialData);
                    const totalAfterDiscount =
                      getProposalQuotedTotal(commercialData);
                    const discount = getDiscountDisplay(
                      commercialData,
                      totalAfterDiscount,
                      discountPercent,
                      discountAmount,
                    );
                    const hasDiscount =
                      discount.percent > 0 || discount.amount > 0;
                    const grandTotalBeforeDiscount = hasDiscount
                      ? discount.before
                      : totalAfterDiscount;
                    const costOfDesignWorks = Math.max(
                      0,
                      grandTotalBeforeDiscount - additionalItemsTotal,
                    );
                    return (
                      <>
                        <tr className="summary-row cp-bold-row">
                          <td>{surchargeNums.costOfWorks}</td>
                          <td colSpan={tableColCount - 2}>
                            Стоимость проектных работ
                          </td>
                          <td>
                            {costOfDesignWorks.toLocaleString("ru-RU") || "0"}
                          </td>
                        </tr>
                        {isEditingAdditionalItems
                          ? tempAdditionalItems.map((item, index) => (
                              <tr
                                key={`additional-edit-${index}`}
                                className="additional-item-edit-row cp-bold-row"
                              >
                                <td>{surchargeNums.additionalStart + index}</td>
                                <td>
                                  <div className="additional-item-name-cell">
                                    <input
                                      type="text"
                                      value={item.name}
                                      onChange={(e) =>
                                        updateAdditionalItem(
                                          index,
                                          "name",
                                          e.target.value,
                                        )
                                      }
                                      className="payment-input additional-item-name-input"
                                      placeholder="Название надбавки"
                                      disabled={!effectiveIsEditable}
                                    />
                                    <button
                                      type="button"
                                      className="remove-payment-btn"
                                      onClick={() =>
                                        removeAdditionalItem(index)
                                      }
                                      title="Удалить"
                                      disabled={!effectiveIsEditable}
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                      >
                                        <path
                                          d="M3 6H13"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                        <path
                                          d="M5 6V14C5 14.55 5.45 15 6 15H10C10.55 15 11 14.55 11 14V6"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <div className="additional-item-value-cell">
                                    <div className="discount-mode-toggle">
                                      <button
                                        type="button"
                                        className={
                                          item.mode === "percent"
                                            ? "active"
                                            : ""
                                        }
                                        onClick={() =>
                                          handleAdditionalItemModeChange(
                                            index,
                                            "percent",
                                          )
                                        }
                                        disabled={!effectiveIsEditable}
                                      >
                                        %
                                      </button>
                                      <button
                                        type="button"
                                        className={
                                          item.mode === "amount" ? "active" : ""
                                        }
                                        onClick={() =>
                                          handleAdditionalItemModeChange(
                                            index,
                                            "amount",
                                          )
                                        }
                                        disabled={!effectiveIsEditable}
                                      >
                                        тг
                                      </button>
                                    </div>
                                    <input
                                      type="number"
                                      value={
                                        item.mode === "percent"
                                          ? item.percent
                                          : item.amount
                                      }
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const next =
                                          Number.isFinite(val) && val >= 0
                                            ? val
                                            : 0;
                                        updateAdditionalItem(
                                          index,
                                          item.mode === "percent"
                                            ? "percent"
                                            : "amount",
                                          item.mode === "percent"
                                            ? Math.min(next, 100)
                                            : next,
                                        );
                                      }}
                                      onFocus={selectPercentInputValue}
                                      onClick={selectPercentInputValue}
                                      className="payment-input"
                                      min="0"
                                      max={
                                        item.mode === "percent"
                                          ? "100"
                                          : undefined
                                      }
                                      step={
                                        item.mode === "percent" ? "0.01" : "1"
                                      }
                                      disabled={!effectiveIsEditable}
                                    />
                                  </div>
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                                {!hideFloorCoefficient && <td></td>}
                                <td>
                                  {item.mode === "percent"
                                    ? `+${Number(item.percent) || 0}%`
                                    : ""}
                                </td>
                                <td>
                                  {item.mode === "amount"
                                    ? (Number(item.amount) || 0).toLocaleString(
                                        "ru-RU",
                                      )
                                    : item.price
                                      ? item.price.toLocaleString("ru-RU")
                                      : "—"}
                                </td>
                              </tr>
                            ))
                          : getAdditionalItems(commercialData).map(
                              (item, index) => {
                                const percent =
                                  Number(item.percent ?? item.percentage) || 0;
                                const amount = Number(item.amount) || 0;
                                const price = getAdditionalItemPrice(item);
                                const totalDisplay =
                                  price > 0
                                    ? price.toLocaleString("ru-RU")
                                    : percent > 0
                                      ? `+${percent}%`
                                      : amount.toLocaleString("ru-RU");
                                return (
                                  <tr
                                    key={`additional-${item.order_index ?? index}`}
                                    className="cp-bold-row"
                                  >
                                    <td>
                                      {surchargeNums.additionalStart + index}
                                    </td>
                                    <td>{item.name || item.title}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    {!hideFloorCoefficient && <td></td>}
                                    <td>{percent > 0 ? `+${percent}%` : ""}</td>
                                    <td>{totalDisplay || "0"}</td>
                                  </tr>
                                );
                              },
                            )}
                        {isEditingAdditionalItems && (
                          <tr className="add-element-row">
                            <td colSpan={tableColCount}>
                              <button
                                type="button"
                                className="add-payment-btn"
                                onClick={addAdditionalItem}
                                disabled={!effectiveIsEditable}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M8 2V14M2 8H14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Добавить надбавку
                              </button>
                            </td>
                          </tr>
                        )}
                        <tr className="grand-total cp-bold-row">
                          <td>{surchargeNums.grandTotal}</td>
                          <td colSpan={tableColCount - 2}>Итого:</td>
                          <td>
                            {grandTotalBeforeDiscount.toLocaleString("ru-RU") ||
                              "0"}
                          </td>
                        </tr>
                        {hasDiscount && (
                          <>
                            <tr className="discount-row">
                              <td colSpan={tableColCount - 1}>
                                {discount.percent > 0
                                  ? `Скидка (${discount.percent}%)`
                                  : "Скидка"}
                              </td>
                              <td>
                                -
                                {discount.amount.toLocaleString("ru-RU", {
                                  maximumFractionDigits: 0,
                                })}
                              </td>
                            </tr>
                            <tr className="grand-total cp-bold-row">
                              <td colSpan={tableColCount - 1}>Итого со скидкой</td>
                              <td>
                                {totalAfterDiscount.toLocaleString("ru-RU") ||
                                  "0"}
                              </td>
                            </tr>
                          </>
                        )}
                        {commercialData.nds_percent > 0 &&
                          (ndsIncluded ? (
                            <>
                              <tr className="nds-row">
                                <td colSpan={tableColCount - 1}>
                                  в т.ч. НДС ({commercialData.nds_percent}%)
                                </td>
                                <td>
                                  {commercialData.nds_sum?.toLocaleString(
                                    "ru-RU",
                                  ) || "0"}
                                </td>
                              </tr>
                              <tr className="nds-row">
                                <td colSpan={tableColCount - 1}>Итого без НДС</td>
                                <td>
                                  {commercialData.total_without_nds?.toLocaleString(
                                    "ru-RU",
                                  ) || "0"}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <>
                              <tr className="nds-row">
                                <td colSpan={tableColCount - 1}>
                                  НДС ({commercialData.nds_percent}%)
                                </td>
                                <td>
                                  {commercialData.nds_sum?.toLocaleString(
                                    "ru-RU",
                                  ) || "0"}
                                </td>
                              </tr>
                              <tr className="grand-total-with-nds cp-bold-row">
                                <td colSpan={tableColCount - 1}>Итого с НДС</td>
                                <td>
                                  {commercialData.total_with_nds?.toLocaleString(
                                    "ru-RU",
                                  ) || "0"}
                                </td>
                              </tr>
                            </>
                          ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>
              {isEditingAdditionalItems && (
                <div className="edit-actions">
                  <button
                    className="save-btn"
                    onClick={handleSaveAdditionalItems}
                    disabled={!effectiveIsEditable || isSavingAdditionalItems}
                  >
                    {isSavingAdditionalItems ? (
                      "Сохранение..."
                    ) : (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M13 2L6 9L3 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Сохранить надбавки
                      </>
                    )}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={handleCancelEditAdditionalItems}
                    disabled={!effectiveIsEditable || isSavingAdditionalItems}
                  >
                    Отмена
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">Нет данных для отображения</div>
          )}
        </div>

        {/* Таблица графика платежей */}
        <div
          className={`payment-table ${
            isEditingPaymentSchedule ? "editing" : ""
          }`}
        >
          <div className="payment-table-header">
            <div>
              <h3>График платежей</h3>
              {isEditingPaymentSchedule && (
                <div className="total-amount-info">
                  <span className="total-amount-label">
                    Общая сумма для расчета:{" "}
                  </span>
                  <span className="total-amount-value">
                    {getTotalAmount().toLocaleString("ru-RU")} тенге
                  </span>
                </div>
              )}
            </div>
            {!isEditingPaymentSchedule && (
              <button
                className="edit-payment-schedule-btn"
                onClick={handleStartEditPaymentSchedule}
                title="Редактировать график платежей"
                disabled={!effectiveIsEditable}
                style={{
                  cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                  opacity: !effectiveIsEditable ? 0.6 : 1,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 2L14 4L5 13H2V10L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Редактировать
              </button>
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Наименование работ</th>
                <th>В процентном соотношении</th>
                <th>Стоимость, тг</th>
                <th>Срок платежа</th>
                {isEditingPaymentSchedule && <th>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {isEditingPaymentSchedule ? (
                <>
                  {tempPaymentSchedule.map((payment, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={payment.work_name}
                          onChange={(e) =>
                            updatePaymentItem(
                              index,
                              "work_name",
                              e.target.value,
                            )
                          }
                          className="payment-input"
                          placeholder="Наименование работ"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td className="percentage-cell">
                        <input
                          type="number"
                          value={payment.percentage}
                          onChange={(e) =>
                            handleNumberFieldInput(index, "percentage", e)
                          }
                          onFocus={handleNumberFieldFocus}
                          className={`payment-input ${
                            calculatingFields.has(`${index}-percentage`)
                              ? "calculating"
                              : ""
                          }`}
                          placeholder="0"
                          min="0"
                          max="100"
                          step="1"
                          title="При изменении процента сумма пересчитается автоматически"
                          disabled={!effectiveIsEditable}
                        />
                        <span className="percentage-symbol">%</span>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={payment.price}
                          onChange={(e) =>
                            handleNumberFieldInput(index, "price", e)
                          }
                          onFocus={handleNumberFieldFocus}
                          className={`payment-input ${
                            calculatingFields.has(`${index}-price`)
                              ? "calculating"
                              : ""
                          }`}
                          placeholder="0"
                          min="0"
                          title="При изменении суммы процент пересчитается автоматически"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td>
                        <textarea
                          value={payment.deadline_description}
                          onChange={(e) =>
                            updatePaymentItem(
                              index,
                              "deadline_description",
                              e.target.value,
                            )
                          }
                          className="payment-textarea"
                          placeholder="Описание срока платежа"
                          rows="2"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          className="remove-payment-btn"
                          onClick={() => removePaymentItem(index)}
                          title="Удалить"
                          disabled={!effectiveIsEditable}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M3 6H5H13"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 6V4M8 4H6M8 4H10"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 6V14C5 14.5523 5.44772 15 6 15H10C10.5523 15 11 14.5523 11 14V6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="add-element-row">
                    <td colSpan={isEditingPaymentSchedule ? "6" : "5"}>
                      <button
                        className="add-payment-btn"
                        onClick={addPaymentItem}
                        disabled={!effectiveIsEditable}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M8 2V14M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Добавить элемент
                      </button>
                    </td>
                  </tr>
                  {tempPaymentSchedule.length > 0 && (
                    <tr className="grand-total">
                      <td colSpan="3">ИТОГО</td>
                      <td>
                        {tempPaymentSchedule
                          .reduce(
                            (sum, payment) => sum + (payment.price || 0),
                            0,
                          )
                          .toLocaleString("ru-RU")}
                      </td>
                      <td colSpan={isEditingPaymentSchedule ? "2" : "1"}></td>
                    </tr>
                  )}
                </>
              ) : commercialData?.cp_payment_schedule?.length > 0 ? (
                (() => {
                  const schedule = alignPaymentScheduleToTotal(
                    commercialData.cp_payment_schedule,
                    getPaymentScheduleTotal(commercialData),
                  );
                  const scheduleTotal = schedule.reduce(
                    (sum, payment) => sum + (Number(payment.price) || 0),
                    0,
                  );
                  return (
                    <>
                      {schedule.map((payment, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{payment.work_name}</td>
                          <td>{payment.percentage}%</td>
                          <td>
                            {payment.price?.toLocaleString("ru-RU") || "0"}
                          </td>
                          <td>{payment.deadline_description}</td>
                        </tr>
                      ))}
                      <tr className="grand-total">
                        <td colSpan="3">ИТОГО</td>
                        <td>{scheduleTotal.toLocaleString("ru-RU")}</td>
                        <td></td>
                      </tr>
                    </>
                  );
                })()
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    Нет данных о графике платежей
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Кнопки сохранения/отмены для графика платежей */}
          {isEditingPaymentSchedule && (
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={handleSavePaymentSchedule}
                disabled={!effectiveIsEditable || isSavingPaymentSchedule}
              >
                {isSavingPaymentSchedule ? (
                  "Сохранение..."
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 2L6 9L3 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Сохранить график платежей
                  </>
                )}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEditPaymentSchedule}
                disabled={!effectiveIsEditable || isSavingPaymentSchedule}
              >
                Отмена
              </button>
            </div>
          )}
        </div>

        {/* Дополнительная информация (редактируемые блоки до реквизитов) */}
        <div className="additional-info">
          <div className="additional-info-editable-block">
            {isEditingAdditionalInfo ? (
              <>
                {infoSections.map((section, sectionIndex) => (
                  <div
                    className="info-section"
                    key={`edit-section-${sectionIndex}`}
                  >
                    <div className="info-section-edit-header">
                      <label className="edit-label">Заголовок:</label>
                      <button
                        type="button"
                        className="list-item-remove-btn"
                        onClick={() => removeInfoSection(sectionIndex)}
                        title="Удалить блок"
                      >
                        Удалить блок
                      </button>
                    </div>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) =>
                        updateInfoSection(sectionIndex, (current) => ({
                          ...current,
                          title: e.target.value,
                        }))
                      }
                      className="edit-input edit-input-block"
                      placeholder="Заголовок блока"
                    />
                    <ol className="editable-list">
                      {(section.items || []).map((item, itemIndex) => (
                        <li
                          key={`edit-item-${sectionIndex}-${itemIndex}`}
                          className="editable-list-item"
                        >
                          <div className="editable-list-item-fields">
                            <textarea
                              value={item.name}
                              onChange={(e) =>
                                updateInfoSectionItem(
                                  sectionIndex,
                                  itemIndex,
                                  "name",
                                  e.target.value,
                                )
                              }
                              className="edit-textarea-list"
                              rows={2}
                              placeholder="Пункт"
                            />
                            <textarea
                              value={item.description}
                              onChange={(e) =>
                                updateInfoSectionItem(
                                  sectionIndex,
                                  itemIndex,
                                  "description",
                                  e.target.value,
                                )
                              }
                              className="edit-textarea-list"
                              rows={2}
                              placeholder="Описание (необязательно)"
                            />
                          </div>
                          <button
                            type="button"
                            className="list-item-remove-btn"
                            onClick={() =>
                              removeInfoSectionItem(sectionIndex, itemIndex)
                            }
                            title="Удалить пункт"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M2 4H14M5 4V2H11V4M6 7V11M10 7V11M3 4L4 14H12L13 4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Удалить
                          </button>
                        </li>
                      ))}
                    </ol>
                    <button
                      type="button"
                      className="list-add-btn"
                      onClick={() => addInfoSectionItem(sectionIndex)}
                    >
                      + Добавить пункт
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="list-add-btn list-add-section-btn"
                  onClick={addInfoSection}
                >
                  + Добавить блок
                </button>

                <div className="info-section">
                  <label className="edit-label">Сроки:</label>
                  <input
                    type="text"
                    value={deadlinesText}
                    onChange={(e) => setDeadlinesText(e.target.value)}
                    className="edit-input edit-input-block"
                    placeholder="Сроки согласно утвержденному графику проектирования."
                  />
                </div>

                <div className="info-section">
                  <label className="edit-label">Исполнитель:</label>
                  <input
                    type="text"
                    value={performerName}
                    onChange={(e) => setPerformerName(e.target.value)}
                    className="edit-input"
                    placeholder="Ф.И.О."
                    style={{ maxWidth: 280 }}
                  />
                </div>
                <div className="info-section">
                  <label className="edit-label">Дата:</label>
                  <input
                    type="text"
                    value={signatureDate}
                    onChange={(e) => setSignatureDate(e.target.value)}
                    className="edit-input"
                    placeholder="ДД.ММ.ГГГГ"
                    style={{ width: 120 }}
                  />
                </div>

                <div className="edit-actions">
                  <button
                    type="button"
                    onClick={handleSaveAdditionalInfo}
                    disabled={isSavingAdditionalInfo}
                    className="save-btn"
                  >
                    {isSavingAdditionalInfo ? "Сохранение…" : "Сохранить"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEditAdditionalInfo}
                    disabled={isSavingAdditionalInfo}
                    className="cancel-btn"
                  >
                    Отмена
                  </button>
                </div>
              </>
            ) : (
              <>
                {infoSections.map((section, sectionIndex) => (
                  <div
                    className={`info-section${
                      sectionIndex === 0 ? " info-section-with-edit" : ""
                    }`}
                    key={`view-section-${sectionIndex}`}
                  >
                    <div className="display-field display-field-inline">
                      <h4>{section.title}</h4>
                      {sectionIndex === 0 && effectiveIsEditable && (
                        <button
                          type="button"
                          className="mode-btn edit-additional-btn"
                          onClick={() => setIsEditingAdditionalInfo(true)}
                          title="Редактировать блок"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M12 2L14 4L5 13H2V10L12 2Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <ol>
                      {(section.items || []).map((item, itemIndex) => (
                        <li key={`view-item-${sectionIndex}-${itemIndex}`}>
                          {item.name}
                          {item.description ? (
                            <div className="info-item-description">
                              {item.description}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}

                <div
                  className={`info-section${
                    infoSections.length === 0 ? " info-section-with-edit" : ""
                  }`}
                >
                  <div className="display-field display-field-inline">
                    <p>{deadlinesText}</p>
                    {infoSections.length === 0 && effectiveIsEditable && (
                      <button
                        type="button"
                        className="mode-btn edit-additional-btn"
                        onClick={() => setIsEditingAdditionalInfo(true)}
                        title="Редактировать блок"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M12 2L14 4L5 13H2V10L12 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="info-section">
            <h4>
              Реквизиты «{getCounterpartyLabel(selectedDesignerCounterparty)}»:
            </h4>
            <p>
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
              <br />
              БИН/ИИН {selectedDesignerCounterparty?.bin_or_inn}, ИИК
              KZ226017111000001238, БИК HSBKKZKX, АО Народный банк Казахстана
              <br />
              тел.: {selectedDesignerCounterparty?.phone}
            </p>
          </div>
        </div>

        {/* Подписи (Исполнитель и дата подтягиваются из редактируемого блока выше) */}
        <div className="signatures">
          <div className="signature-block">
            <div className="signature-images">
              {selectedDesignerCounterparty?.signature_image_name && (
                <div
                  className="signature-image-editable"
                  style={{
                    left: `${signatureLayout.signature.x}px`,
                    top: `${signatureLayout.signature.y}px`,
                    width: `${signatureLayout.signature.w}px`,
                    height: `${signatureLayout.signature.h}px`,
                  }}
                  onMouseDown={startSignatureDrag("signature", "move")}
                >
                  <img
                    src={getImageServiceUrl(
                      selectedDesignerCounterparty.signature_image_name,
                    )}
                    alt="Подпись"
                    className="signature-image signature-image-signature"
                  />
                  {effectiveIsEditable && (
                    <span
                      className="signature-resize-handle"
                      onMouseDown={startSignatureDrag("signature", "resize")}
                    />
                  )}
                </div>
              )}
              {selectedDesignerCounterparty?.seal_image_name && (
                <div
                  className="signature-image-editable"
                  style={{
                    left: `${signatureLayout.seal.x}px`,
                    top: `${signatureLayout.seal.y}px`,
                    width: `${signatureLayout.seal.w}px`,
                    height: `${signatureLayout.seal.h}px`,
                  }}
                  onMouseDown={startSignatureDrag("seal", "move")}
                >
                  <img
                    src={getImageServiceUrl(
                      selectedDesignerCounterparty.seal_image_name,
                    )}
                    alt="Печать"
                    className="signature-image signature-image-seal"
                  />
                  {effectiveIsEditable && (
                    <span
                      className="signature-resize-handle"
                      onMouseDown={startSignatureDrag("seal", "resize")}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="signature-line" />
            {showDirectorTitle && (
              <div className="signature-position">Директор</div>
            )}
            <div className="signature-name">{selectedDesignerDirectorName}</div>
          </div>
          <div className="performer">
            <div>Исполнитель - {performerName}</div>
          </div>
          <div className="date">от {signatureDate}</div>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="commercial-offer-actions">
        <button
          className="btn-send-approval"
          onClick={() => setShowApprovalModal(true)}
          disabled={
            !effectiveIsEditable || isSubmittingApproval || !commercialData
          }
        >
          {isSubmittingApproval ? "Отправка..." : "Отправить на согласование"}
        </button>
        {/* <button className="btn-save" disabled={!effectiveIsEditable}>
          Сохранить
        </button> */}
        {/* <button className="btn-print-pdf">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M14 2H2V14H14V2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 9H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 12H8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Печать PDF
        </button> */}
      </div>

      {/* История версий */}
      <VersionHistoryDrawer
        open={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        projectId={projectId}
        type="commercial"
      />

      {/* Модальное окно согласования */}
      <CommercialOfferApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        isSubmitting={isSubmittingApproval}
      />

      {/* Модальные окна создания контрагентов */}
      <AddCounterpartyModal
        open={showAddDesignerModal}
        onClose={() => setShowAddDesignerModal(false)}
        defaultType="organization"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getCounterpartyLabel(newCounterparty),
          };
          setDesignersCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedDesigner(newOption);
          if (selectedRecipient?.value) {
            saveDesignerAndRecipient();
          }
        }}
      />

      <AddCounterpartyModal
        open={showAddRecipientModal}
        onClose={() => setShowAddRecipientModal(false)}
        defaultType="counterparty"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getCounterpartyLabel(newCounterparty),
          };
          setRecipientsCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedRecipient(newOption);
          if (selectedDesigner?.value) {
            saveDesignerAndRecipient();
          }
        }}
      />
    </div>
  );
};

export default CommercialOffer;
